import {SvgIcon, type SvgIconProps} from '@mui/material';
import {forwardRef, type FunctionComponent, type SVGProps} from 'react';

import addCircleIcon from './add-circle.svg?react';
import addFolderIcon from './add-folder.svg?react';
import alertIcon from './alert.svg?react';
import cancelIcon from './cancel.svg?react';
import {
    AddWaypointIcon,
    AreaIcon,
    ArrowDropDownIcon,
    CenterIcon,
    CollapseIcon,
    DeleteIcon,
    DirectionsIcon,
    ExploreIcon,
    ExportIcon,
    FacebookIcon,
    FolderIcon,
    FullscreenIcon,
    GoPremiumIcon,
    HelpIcon,
    ImportIcon,
    InfoIcon,
    InfoIconOutlined,
    LayersIcon,
    MapLayersIcon,
    MyLocationIcon,
    OpenDetailsIcon,
    OverlaysIcon,
    PrintIcon,
    RouteIcon,
    SettingsIcon,
    ShareIcon,
    TwitterIcon,
    VisibilityIcon,
    VisibilityOffIcon,
    WaypointIcon,
    ZoomInIcon,
    ZoomOutIcon,
} from './controls';
import copyIcon from './copy.svg?react';
import allItemsIcon from './data/all-items.svg?react';
import areaOutlinedIcon from './data/areas.svg?react';
import folderOutlineIcon from './data/folder.svg?react';
import routeOutlinedIcon from './data/routes.svg?react';
import trackOutlinedIcon from './data/tracks.svg?react';
import waypointOutlinedIcon from './data/waypoints.svg?react';
import editIcon from './edit.svg?react';
import embedIcon from './embed.svg?react';
import filterIcon from './filter.svg?react';
import addFolderOutlineIcon from './folder-add-outline.svg?react';
import removeFolderOutlineIcon from './folder-remove-outline.svg?react';
import folderSharedIcon from './folder-shared.svg?react';
import mapLegend from './map-legend.svg?react';
import moreIcon from './more.svg?react';
import pencilFilledIcon from './pencil-filled.svg?react';
import personFilledIcon from './person-filled.svg?react';
import removeFolderIcon from './remove-folder.svg?react';
import searchIcon from './search.svg?react';
import sortIcon from './sort.svg?react';

// SvgIcon sets dimensions relative to font-size. We want our default
// display size to correspond to the icon's viewbox of 24x24.
const svgIconStyle = {
    fontSize: 24,
};

const iconNameMap = {
    'add-circle': addCircleIcon,
    'add-folder': addFolderIcon,
    'add-folder-outline': addFolderOutlineIcon,
    'add-waypoint': AddWaypointIcon,
    'all-items': allItemsIcon,
    alert: alertIcon,
    area: AreaIcon,
    'area-outline': areaOutlinedIcon,
    'arrow-drop-down': ArrowDropDownIcon,
    cancel: cancelIcon,
    center: CenterIcon,
    collapse: CollapseIcon,
    copy: copyIcon,
    delete: DeleteIcon,
    directions: DirectionsIcon,
    edit: editIcon,
    embed: embedIcon,
    explore: ExploreIcon,
    export: ExportIcon,
    facebook: FacebookIcon,
    filter: filterIcon,
    folder: FolderIcon,
    'folder-outline': folderOutlineIcon,
    'folder-shared': folderSharedIcon,
    fullscreen: FullscreenIcon,
    'go-premium': GoPremiumIcon,
    help: HelpIcon,
    info: InfoIcon,
    import: ImportIcon,
    layers: LayersIcon,
    overlays: OverlaysIcon,
    print: PrintIcon,
    'info-outline': InfoIconOutlined,
    route: RouteIcon,
    settings: SettingsIcon,
    'map-layers': MapLayersIcon,
    'map-legend': mapLegend,
    more: moreIcon,
    'my-location': MyLocationIcon,
    'open-details': OpenDetailsIcon,
    'pencil-filled': pencilFilledIcon,
    'person-filled': personFilledIcon,
    'remove-folder': removeFolderIcon,
    'remove-folder-outline': removeFolderOutlineIcon,
    'route-outline': routeOutlinedIcon,
    search: searchIcon,
    share: ShareIcon,
    sort: sortIcon,
    'track-outline': trackOutlinedIcon,
    twitter: TwitterIcon,
    visibility: VisibilityIcon,
    'visibility-off': VisibilityOffIcon,
    waypoint: WaypointIcon,
    'waypoint-outline': waypointOutlinedIcon,
    'zoom-in': ZoomInIcon,
    'zoom-out': ZoomOutIcon,
} satisfies Record<string, FunctionComponent<SVGProps<SVGSVGElement>>>;

export interface GaiaIconProps extends SvgIconProps {
    name: keyof typeof iconNameMap;
}

const GaiaIcon = (props: GaiaIconProps, ref: React.Ref<SVGSVGElement>) => {
    const {name, ...iconProps} = props;
    const iconComponent = iconNameMap[name];
    return <SvgIcon sx={svgIconStyle} component={iconComponent} ref={ref} {...iconProps} />;
};

// Ref must be forwarded in order to support SubscriptionTooltip
export default forwardRef(GaiaIcon);
